import { formatCount } from './format';

/**
 * A hand-rolled SVG histogram — CLM-81 (D-81-1). One `<rect>` per bucket,
 * value labels above, bucket labels below; `viewBox` fixed at `0 0 480 200`
 * and `width="100%"` so it scales with the card. The fill is `var(--acc)`;
 * no colour literal.
 *
 * Accessibility: the `<svg>` is `role="img"` with an `aria-label` naming the
 * chart, and a visually hidden `<table className="sr-only">` sibling carries the
 * same numbers for assistive technology. A zero total still draws the axis and
 * says so in copy.
 */

export interface HistogramBucket {
  readonly key: string;
  readonly label: string;
  readonly n: number;
}

const W = 480;
const H = 200;
const PAD_LEFT = 16;
const PAD_RIGHT = 16;
const PAD_TOP = 24;
const PAD_BOTTOM = 36;

export function BarHistogram({
  title,
  buckets,
  emptyCopy,
}: {
  readonly title: string;
  readonly buckets: readonly HistogramBucket[];
  readonly emptyCopy: string;
}) {
  const total = buckets.reduce((acc, b) => acc + b.n, 0);
  const max = Math.max(1, ...buckets.map((b) => b.n));
  const innerW = W - PAD_LEFT - PAD_RIGHT;
  const innerH = H - PAD_TOP - PAD_BOTTOM;
  const slot = buckets.length === 0 ? innerW : innerW / buckets.length;
  const barW = slot * 0.6;
  const baseline = PAD_TOP + innerH;

  return (
    <div className="chart">
      <svg role="img" aria-label={title} viewBox={`0 0 ${String(W)} ${String(H)}`} width="100%" className="histogram">
        <line x1={PAD_LEFT} x2={W - PAD_RIGHT} y1={baseline} y2={baseline} className="axis" />
        {buckets.map((bucket, i) => {
          const h = total === 0 ? 0 : (bucket.n / max) * innerH;
          const x = PAD_LEFT + slot * i + (slot - barW) / 2;
          const y = baseline - h;
          return (
            <g key={bucket.key} data-bucket={bucket.key}>
              <rect x={x} y={y} width={barW} height={h} rx={3} />
              <text x={x + barW / 2} y={y - 6} textAnchor="middle" className="value">
                {formatCount(bucket.n)}
              </text>
              <text x={x + barW / 2} y={baseline + 18} textAnchor="middle" className="label">
                {bucket.label}
              </text>
            </g>
          );
        })}
      </svg>
      {total === 0 ? <p className="mut">{emptyCopy}</p> : null}
      <table className="sr-only">
        <caption>{title}</caption>
        <thead>
          <tr>
            <th scope="col">Bucket</th>
            <th scope="col">Contracts</th>
          </tr>
        </thead>
        <tbody>
          {buckets.map((bucket) => (
            <tr key={bucket.key}>
              <th scope="row">{bucket.label}</th>
              <td>{formatCount(bucket.n)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
